import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  Checkbox,
} from '@mui/material';

import {
  DaysOfWeekCheckboxes,
  Loading,
  ZoneCategorySelector,
  ZoneCategoryTargetFrequencyInput,
  ZoneCategoryTargetIntervalSelector,
  ZoneScheduleSlider,
} from 'components';
import { zoneLocations } from 'consts';
import { useBuildings } from 'pages/buildings/buildings.hook';
import { useBuilding, useFetchBuildingQuery, useFetchZoneQuery } from 'store/apis';
import { getBuildingLocation } from 'utils';
import { useZoneFormManager } from './zone-form.hook';
import { BuildingAreaSelector } from '../../../components/selectors/building-area-selector';
import { useAppSelector } from '../../../store/store.hook';
import { selectCurrentUserInfo } from '../../../store/slices';
import { EZoneTargetCleaningType } from '../../../enums';

interface ZoneFormProps {
  initialBuildingId?: number | null;
  zoneId?: number | null;
  onInit?: (loading: boolean) => void;
  onSubmitting?: (loading: boolean) => void;
  next?: (data?: any, copyZonesFlag?: boolean) => void;
  isCopy?: boolean;
}

export const ZoneForm = React.forwardRef(
  (
    { initialBuildingId, zoneId, isCopy = false, onInit = () => {}, onSubmitting = () => {}, next }: ZoneFormProps,
    ref,
  ) => {
    const theme = useTheme();
    const { orgSlug } = useAppSelector(selectCurrentUserInfo);
    const buildingId = useRef<number>();
    const [loadingForm, setLoadingForm] = useState(false);

    const [skipFetchZone, setSkipFetchZone] = useState<boolean>(true);
    const [skipFetchBuilding, setSkipFetchBuilding] = useState<boolean>(true);
    const { data: zone, isLoading: loading_zone } = useFetchZoneQuery(
      { orgSlug, id: zoneId as number },
      { skip: skipFetchZone },
    );
    const { data: building, isLoading: loading_building } = useFetchBuildingQuery(
      { orgSlug, id: buildingId.current as number },
      { skip: skipFetchBuilding },
    );

    useEffect(() => {
      if (initialBuildingId) {
        handleBuildingChange(initialBuildingId);
      }
    }, []);

    useEffect(() => {
      if (zoneId) {
        setSkipFetchZone(false);
      }

      if (buildingId.current) {
        setSkipFetchBuilding(false);
      }
    }, [zoneId, buildingId.current]);

    useEffect(() => {
      if (loading_building || loading_zone) {
        setLoadingForm(true);
      } else {
        setLoadingForm(false);
      }
    }, [loading_zone, loading_building]);

    useEffect(() => {
      if (zone) {
        handleBuildingChange(zone.building_id);
        const schedule = zone.schedule.map((s: any) => {
          const [hours, minutes] = s.time.split(':').map((t: any) => parseInt(t, 10));
          return minutes + hours * 60;
        });
        const updateFormValues = {
          ...formManager.values,
          name: zone.name,
          clean_code_zone_category_id: zone.clean_code_zone_category_id,
          building_id: zone.building_id,
          building_area_id: zone.building_area_id,
          building_floor_id: zone.building_floor_id,
          target_cleaning_type: zone.target_cleaning_type,
          target_interval: zone.target_interval,
          target_frequency: zone.target_frequency,
          cleaning_interval_seconds: zone.cleaning_interval_seconds,
          days_active: zone.days_active,
          schedule,
        };
        formManager.setValues(updateFormValues);
      }
    }, [zone]);

    const handleBuildingChange = (id: number | null) => {
      if (id) {
        buildingId.current = id;
        formManager.setFieldValue('building_id', id);
      }
    };

    const [{ formManager, loading_submit }] = useZoneFormManager({
      building,
      zone,
      next,
      isCopy,
    });

    React.useImperativeHandle(ref, () => ({ onSubmit: () => formManager.handleSubmit() }), []);
    React.useEffect(() => onInit(loadingForm), [loadingForm]);
    React.useEffect(() => onSubmitting(loading_submit), [loading_submit]);

    return (
      <form onSubmit={formManager.handleSubmit} autoComplete="off">
        <Box paddingY={2}>
          <Grid container rowSpacing={3} columnSpacing={2}>
            {!zoneId ? (
              <Grid item xs={12}>
                <Box marginBottom={1} display="flex" alignItems="center">
                  <Typography variant="caption" color="textSecondary">
                    Suggested Zone Names
                  </Typography>
                </Box>

                <Box display="flex" flexWrap="wrap" alignItems="center" marginLeft={-1} marginRight={-1}>
                  {zoneLocations.map((zoneLocation) => (
                    <Box key={`zone-location-${zoneLocation.id}`} padding={1}>
                      <Chip
                        label={zoneLocation.name}
                        color="primary"
                        onClick={() => {
                          formManager.setFieldValue('name', zoneLocation.name);
                        }}
                        variant={formManager.values.name.trim() === zoneLocation.name ? 'filled' : 'outlined'}
                      />
                    </Box>
                  ))}
                </Box>
              </Grid>
            ) : null}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Zone name"
                variant="outlined"
                value={formManager.values.name || ''}
                onChange={formManager.handleChange('name')}
                error={!!(formManager.errors.name && formManager.touched.name)}
                helperText={formManager.touched.name ? formManager.errors.name : null}
              />
            </Grid>

            <Grid item xs={12}>
              <BuildingSelector
                value={formManager.values.building_id}
                isError={!!(formManager.errors.building_id && formManager.touched.building_id)}
                helperText={(formManager.touched.building_id ? formManager.errors.building_id : '') as string}
                onChangeBuilding={(id) => handleBuildingChange(id)}
              />
            </Grid>

            {building && (
              <>
                <Grid item xs={12}>
                  {formManager.values.building_id ? (
                    <FloorSelector
                      value={formManager.values.building_floor_id}
                      buildingId={formManager.values.building_id}
                      isError={!!(formManager.errors.building_floor_id && formManager.touched.building_floor_id)}
                      helperText={
                        (formManager.touched.building_floor_id ? formManager.errors.building_floor_id : '') as string
                      }
                      onChangeFloor={(floorId) => formManager.setFieldValue('building_floor_id', floorId)}
                    />
                  ) : (
                    <Tooltip title="Building must be selected first" arrow>
                      <Autocomplete
                        options={[]}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select a floor..."
                            inputProps={{
                              ...params.inputProps,
                              sx: {
                                cursor: 'not-allowed',
                              },
                            }}
                          />
                        )}
                        fullWidth
                        disabled
                      />
                    </Tooltip>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <BuildingAreaSelector
                    building={building}
                    value={formManager.values.building_area_id}
                    onChange={(value) => formManager.setFieldValue('building_area_id', value)}
                    error={!!(formManager.errors.building_area_id && formManager.touched.building_area_id)}
                    helperText={
                      (formManager.touched.building_area_id ? formManager.errors.building_area_id : '') as string
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <ZoneCategorySelector
                    value={formManager.values.clean_code_zone_category_id}
                    onChange={(value) => {
                      formManager.setFieldValue('clean_code_zone_category_id', value);
                    }}
                    error={
                      !!(
                        formManager.errors.clean_code_zone_category_id &&
                        formManager.touched.clean_code_zone_category_id
                      )
                    }
                    helperText={
                      (formManager.touched.clean_code_zone_category_id
                        ? formManager.errors.clean_code_zone_category_id
                        : '') as string
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl>
                    <FormLabel>Set Cleaning Target</FormLabel>
                    <RadioGroup
                      defaultValue="none"
                      value={formManager.values.target_cleaning_type}
                      onChange={(e) => formManager.setFieldValue('target_cleaning_type', e.target.value)}
                    >
                      <FormControlLabel
                        value="none"
                        control={<Radio size="small" />}
                        label="No Cleaning Target"
                        componentsProps={{ typography: { fontSize: 12 } }}
                      />
                      <FormControlLabel
                        value="frequency"
                        control={<Radio size="small" />}
                        label="Set Cleaning Frequency"
                        componentsProps={{ typography: { fontSize: 12 } }}
                      />
                      <FormControlLabel
                        value="schedule"
                        control={<Radio size="small" />}
                        label="Set Specific Cleaning Schedule"
                        componentsProps={{ typography: { fontSize: 12 } }}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                {!zone && (
                  <Grid item xs={12} alignItems="left">
                    <FormControl>
                      <FormControlLabel
                        sx={{ color: theme.palette.text.secondary }}
                        control={
                          <Checkbox
                            checked={formManager.values.copy_zones_flag}
                            onChange={(e) => formManager.setFieldValue('copy_zones_flag', e.target.checked)}
                          />
                        }
                        label="Copy Zone to Other Floors"
                      />
                    </FormControl>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', marginBottom: -1 }}>
                      Zone creation requests are queued and may take some time to appear.
                    </Typography>
                  </Grid>
                )}
                {formManager.values.target_cleaning_type === EZoneTargetCleaningType.FREQUENCY && (
                  <Grid container item xs={12} rowSpacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textPrimary" sx={{ marginBottom: -1 }}>
                        How often should these categories be serviced??
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <ZoneCategoryTargetIntervalSelector
                        value={formManager.values.target_interval || ''}
                        onChange={(_, value) => {
                          formManager.setFieldValue('target_interval', value);
                        }}
                        error={!!(formManager.errors.target_interval && formManager.touched.target_interval)}
                        helperText={
                          (formManager.touched.target_interval ? formManager.errors.target_interval : '') as
                            | string
                            | undefined
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textPrimary" sx={{ marginBottom: -1 }}>
                        How many times per interval?
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <ZoneCategoryTargetFrequencyInput
                        value={formManager.values.target_frequency ?? 0}
                        onChange={formManager.handleChange('target_frequency')}
                        onIncrease={() => {
                          let currentValue = formManager.values.target_frequency || 0;

                          if (currentValue < 0) {
                            currentValue = 0;
                          }

                          formManager.setFieldValue('target_frequency', currentValue + 1);
                        }}
                        onDecrease={() => {
                          let currentValue = formManager.values.target_frequency || 0;

                          if (currentValue < 0) {
                            currentValue = 2;
                          }

                          if (currentValue > 1) {
                            formManager.setFieldValue('target_frequency', currentValue - 1);
                          }
                        }}
                        error={!!(formManager.errors.target_frequency && formManager.touched.target_frequency)}
                        helperText={
                          (formManager.touched.target_frequency ? formManager.errors.target_frequency : '') as
                            | string
                            | undefined
                        }
                      />
                    </Grid>
                  </Grid>
                )}
                {formManager.values.target_cleaning_type === EZoneTargetCleaningType.SCHEDULE && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6">Cleaning Schedule</Typography>

                      <Typography variant="caption" color="textSecondary">
                        Pick the days of the week that the zone is active
                      </Typography>
                      <DaysOfWeekCheckboxes
                        label=""
                        value={formManager.values.days_active || ''}
                        onChange={formManager.handleChange('days_active')}
                      />
                    </Grid>

                    <Grid item xs={12} sx={{ overflow: 'hidden' }}>
                      <Typography variant="caption" color="textSecondary">
                        Please pick the times this space will be cleaned
                      </Typography>

                      <ZoneScheduleSlider
                        value={formManager.values.schedule}
                        onChangeValue={(value: number[]) => formManager.setFieldValue('schedule', value)}
                      />

                      {formManager.touched.schedule && formManager.errors.schedule && (
                        <Typography
                          style={{
                            marginTop: 6,
                            marginLeft: 14,
                            fontSize: 12,
                            color: theme.palette.error.main,
                          }}
                        >
                          {formManager.errors.schedule}
                        </Typography>
                      )}

                      <Button
                        variant="text"
                        component="label"
                        sx={{ mt: 2 }}
                        onClick={() =>
                          formManager.setFieldValue('schedule', [...formManager.values.schedule, 0].sort())
                        }
                      >
                        <Typography>● Add more cleaning times</Typography>
                      </Button>
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
        </Box>
      </form>
    );
  },
);

interface BuildingSelectorProps {
  value: number | null;
  isError?: boolean;
  helperText?: string;
  onChangeBuilding?: (value: number | null) => void;
}

const BuildingSelector = ({
  value,
  isError = false,
  helperText = '',
  onChangeBuilding = () => {},
}: BuildingSelectorProps) => {
  const [loading, buildings] = useBuildings();

  return (
    <Autocomplete
      value={value}
      options={buildings.map((item) => item.id)}
      getOptionLabel={(option) => {
        const building = buildings.find((item) => item.id === option);
        let label = '';

        if (building) {
          label = getBuildingLocation(building);
        }

        return label;
      }}
      renderOption={(props, option) => {
        const building = buildings.find((item) => item.id === option);
        let label = '';

        if (building) {
          label = getBuildingLocation(building);
        }

        return (
          <li {...props} key={building?.id || Date.now()}>
            {label}
          </li>
        );
      }}
      onChange={(_, newValue) => {
        onChangeBuilding(newValue || null);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Building"
          placeholder="Select a building..."
          error={isError}
          helperText={helperText}
        />
      )}
      fullWidth
      loading={loading}
      loadingText={<Loading text="Loading buildings..." height={120} />}
    />
  );
};

interface FloorSelectorProps {
  value: number | null;
  buildingId: number;
  isError?: boolean;
  helperText?: string;
  onChangeFloor?: (value: number | null) => void;
}

const FloorSelector = ({
  value,
  buildingId,
  isError = false,
  helperText = '',
  onChangeFloor = () => {},
}: FloorSelectorProps) => {
  const { data: building, isLoading: loading } = useBuilding({ buildingId });

  return (
    <Autocomplete
      value={value}
      options={building?.floors?.map((floor) => floor.id) || []}
      getOptionLabel={(option) => {
        const floor = building?.floors?.find((item) => item.id === option);

        if (floor) {
          return floor.floor;
        }

        return '';
      }}
      onChange={(event, newValue: any) => {
        onChangeFloor(newValue || null);
      }}
      fullWidth
      renderInput={(params) => (
        <TextField {...params} label="Floor" placeholder="Select a floor..." error={isError} helperText={helperText} />
      )}
      loading={loading}
      loadingText={<Loading text="Loading floors..." height={120} />}
    />
  );
};
